import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest, fetchPublicBookingConfig, accountPortalRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/useToast";
import { useNavigate, Link } from 'react-router-dom';
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { ContentCut, CalendarMonth, AccessTime, Person, ExitToApp, Cancel } from "@mui/icons-material";
import ThemeToggle from "@/components/ThemeToggle";
import { formatCents, getStatusChipColor } from "@/lib/utils";
import { dialogActionsSx } from "@/lib/dialogStyles";
import { tryGetPublicSlug } from "@/lib/bookingApi";
import type { Business, BookingWithDetails, Customer } from "@shared/schema";

const ACCOUNT_TOKEN_KEY = "bookflow_account_token";

interface AccountBookingsResponse {
  customer: Customer;
  bookings: BookingWithDetails[];
}

export default function AccountPortal() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [token, setToken] = useState<string | null>(null);
  const [tokenLoaded, setTokenLoaded] = useState(false);
  const [cancelBooking, setCancelBooking] = useState<BookingWithDetails | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get("token");
    if (urlToken) {
      localStorage.setItem(ACCOUNT_TOKEN_KEY, urlToken);
      window.history.replaceState({}, "", "/account");
    }
    const stored = localStorage.getItem(ACCOUNT_TOKEN_KEY);
    setToken(urlToken || stored);
    setTokenLoaded(true);
  }, []);

  const publicSlug = tryGetPublicSlug();
  const { data: business } = useQuery<Business>({
    queryKey: ["public-config", publicSlug],
    queryFn: async () => {
      const br = await fetchPublicBookingConfig(publicSlug!);
      return {
        id: br.companyId,
        name: br.name,
        slug: br.slug,
        timezone: br.timezone,
        currency: br.currency,
        category: "beauty",
        address: null,
        phone: null,
        email: null,
        website: null,
        description: null,
        logoUrl: null,
        coverImageUrl: null,
        accentColor: "#7c3aed",
        minLeadTimeMins: br.minLeadTimeMins,
        maxBookingDays: br.maxBookingHorizonDays,
        slotIncrementMins: br.slotIncrementMins,
        cancellationPolicyText: br.cancellationPolicyText ?? null,
        depositEnabled: br.defaultDepositEnabled,
        defaultDepositType: br.defaultDepositType,
        defaultDepositValue: br.defaultDepositValue,
        bookingPageConfig: null,
        bookingPageDraftConfig: null,
        brandingUpdatedAt: null,
      } as Business;
    },
    enabled: !!publicSlug,
  });

  const { data: accountData, isLoading, error } = useQuery<AccountBookingsResponse>({
    queryKey: ["/api/account/bookings", token],
    queryFn: async () => {
      const res = await accountPortalRequest("GET", "/api/account/bookings", token);
      return res.json();
    },
    enabled: tokenLoaded && !!token,
    retry: false,
  });

  const bookings = accountData?.bookings ?? [];

  const cancelMutation = useMutation({
    mutationFn: (bookingToken: string) =>
      apiRequest("POST", `/api/manage/${bookingToken}`, { action: "cancel" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/account/bookings", token] });
      toast({ title: t("toasts.bookingCancelled") });
      setCancelBooking(null);
    },
    onError: (err: Error) => toast({ title: t("common.error"), description: err.message, variant: "destructive" }),
  });

  function handleLogout() {
    localStorage.removeItem(ACCOUNT_TOKEN_KEY);
    navigate("/account/login");
  }

  useEffect(() => {
    if (tokenLoaded && !token) navigate("/account/login");
  }, [tokenLoaded, token, navigate]);

  useEffect(() => {
    if (error) {
      localStorage.removeItem(ACCOUNT_TOKEN_KEY);
      navigate("/account/login");
    }
  }, [error, navigate]);

  if (!tokenLoaded || isLoading) {
    return (
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default", p: 2, maxWidth: 720, mx: "auto" }}>
        <Skeleton height={48} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={120} sx={{ mb: 2 }} />
        <Skeleton variant="rounded" height={120} />
      </Box>
    );
  }

  const now = new Date();
  const upcoming = bookings
    .filter((b) => new Date(b.startAt) >= now && b.status !== "cancelled")
    .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
  const past = bookings
    .filter((b) => new Date(b.startAt) < now || b.status === "cancelled")
    .sort((a, b) => new Date(b.startAt).getTime() - new Date(a.startAt).getTime());

  function BookingCard({ booking, allowCancel }: { booking: BookingWithDetails; allowCancel?: boolean }) {
    const isPast = new Date(booking.endAt) < now;
    const dep = booking.depositAmountCents ?? 0;
    return (
      <Card variant="outlined" data-testid={`card-account-booking-${booking.id}`}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
                <Typography fontWeight={700}>{booking.service?.name}</Typography>
                <Chip size="small" label={booking.status} color={getStatusChipColor(booking.status)} />
              </Box>
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, typography: "body2", color: "text.secondary" }}>
                {booking.staffMember && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Person fontSize="small" />
                    {booking.staffMember.displayName}
                  </Box>
                )}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <CalendarMonth fontSize="small" />
                  {new Date(booking.startAt).toLocaleDateString("en-GB", { weekday: "short", month: "short", day: "numeric" })}
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <AccessTime fontSize="small" />
                  {new Date(booking.startAt).toLocaleTimeString("en-GB", { hour: "numeric", minute: "2-digit", hour12: true })}
                </Box>
              </Box>
              <Box sx={{ mt: 1, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
                <Typography fontWeight={600}>{formatCents(booking.service?.priceCents || 0, booking.currency || "GBP")}</Typography>
                {dep > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {t("accountPortal.depositPaid", { amount: formatCents(dep, booking.currency || "GBP") })}
                  </Typography>
                )}
              </Box>
            </Box>
            {allowCancel && !isPast && booking.status !== "cancelled" && booking.manageToken && (
              <Button
                size="small"
                color="error"
                variant="outlined"
                startIcon={<Cancel />}
                onClick={() => setCancelBooking(booking)}
                data-testid={`button-cancel-account-booking-${booking.id}`}
              >
                {t("common.cancel")}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar position="static" color="default" elevation={0} sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Toolbar sx={{ maxWidth: 720, mx: "auto", width: "100%" }}>
          <ContentCut color="primary" sx={{ mr: 1 }} />
          <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
            {business?.name || t("common.poweredByDefault")}
          </Typography>
          <ThemeToggle />
          <Button color="inherit" size="small" startIcon={<ExitToApp />} onClick={handleLogout}>
            {t("common.signOut")}
          </Button>
        </Toolbar>
      </AppBar>
      <Box component="main" sx={{ maxWidth: 720, mx: "auto", px: 2, py: 4 }}>
        <Typography variant="h5" fontWeight={700}>
          {t("accountPortal.pageTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t("accountPortal.subtitle")}
        </Typography>

        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          {t("accountPortal.upcoming")}
        </Typography>
        {upcoming.length === 0 ? (
          <Card variant="outlined">
            <CardContent sx={{ py: 6, textAlign: "center" }}>
              <CalendarMonth sx={{ fontSize: 40, color: "action.disabled", mb: 2 }} />
              <Typography color="text.secondary">{t("accountPortal.noUpcoming")}</Typography>
              <Button component={Link} to="/" variant="outlined" sx={{ mt: 2 }} data-testid="button-book-now">
                {t("accountPortal.bookNow")}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mb: 4 }}>
            {upcoming.map((b) => (
              <BookingCard key={b.id} booking={b} allowCancel />
            ))}
          </Box>
        )}

        {past.length > 0 && (
          <>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
              {t("accountPortal.past")}
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {past.slice(0, 10).map((b) => (
                <BookingCard key={b.id} booking={b} />
              ))}
            </Box>
          </>
        )}
      </Box>

      <Dialog open={!!cancelBooking} onClose={() => setCancelBooking(null)}>
        <DialogTitle>{t("accountPortal.cancelDialogTitle")}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {t("accountPortal.cancelDialogBody")}
          </Typography>
          {(cancelBooking?.depositAmountCents ?? 0) > 0 && cancelBooking?.paymentStatus === "paid" && (
            <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
              {t("accountPortal.depositWarning")}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={dialogActionsSx}>
          <Button variant="outlined" onClick={() => setCancelBooking(null)}>
            {t("accountPortal.keepBooking")}
          </Button>
          <Button
            color="error"
            variant="contained"
            disabled={cancelMutation.isPending}
            onClick={() => cancelBooking?.manageToken && cancelMutation.mutate(cancelBooking.manageToken)}
          >
            {t("accountPortal.yesCancel")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
